import { Component, NgZone, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { AuthenticationService } from '../_services/authentication.service';
import { environment } from 'src/environments/environment';
import { markNavLanding } from '../_models/ui-order';
import { PermissionService } from '../_services/permission.service';

// Google Identity Services is loaded globally via index.html.
declare const google: any;

/**
 * A complete 10-digit US number, counted on digits alone so the display
 * formatting is irrelevant. The backend applies the same rule.
 */
export function usPhoneNumberValidator(control: AbstractControl): ValidationErrors | null {
  const digits = (control.value || '').replace(/\D/g, '');
  if (!digits) {
    return null;  // 'required' already covers the empty case.
  }
  return digits.length === 10 ? null : { usPhoneNumber: true };
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  loginForm: UntypedFormGroup;
  otpForm: UntypedFormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  error = '';
  googleError = '';
  hide = true;
  isNative = Capacitor.isNativePlatform();
  private googleInitialized = false;

  // ENG-5362: phone sign-in for merchants who keep forgetting their password.
  // 'password' -> the email/password form; 'phone' -> enter a number;
  // 'otp' -> a code has been sent and we are waiting for it.
  mode: 'password' | 'phone' | 'otp' = 'password';
  otpSubmitted = false;
  otpError = '';
  otpNotice = '';
  resendSeconds = 0;
  private resendTimer: any = null;

  // Visual digit cells driven from the real input value.
  otpDigits: string[] = ['', '', '', '', '', ''];
  otpCursor = 0;
  otpFocused = false;
  @ViewChild('otpInput') otpInputRef!: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private permissionService: PermissionService,
    private ngZone: NgZone
  ) { }

  async ngOnInit() {
    // For native platforms (PAX terminal), default rememberMe to true
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: [this.isNative ? true : false]
    });

    this.otpForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, usPhoneNumberValidator]],
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });

    // get return url from route parameters or default to '/'
    // ENG-5687: '/home' is the landing stop. It opens the first item of the merchant's
    // side-nav order, or Home when there is none. The permission object held right now is
    // the previous user's (or null); the landing waits for a fresh one.
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    let current = null;
    this.permissionService.current.pipe(first()).subscribe(p => current = p);
    markNavLanding(current);

    if (this.isNative) {
      // Native (PAX): saved-credentials auto re-login flow.
      await this.checkAutoLogin();
    } else {
      // Web: restore a valid session or clear stale state, then wire up Google.
      if (this.authenticationService.isLoggedIn()) {
        this.router.navigate([this.returnUrl]);
      } else {
        // reset login status
        this.authenticationService.logout();
      }
      this.initGoogleSignIn();
    }
  }

  private async checkAutoLogin() {
    try {
      const rememberMeEnabled = await this.authenticationService.isRememberMeEnabled();
      const user = this.authenticationService.currentUserValue;
      const tokenExpired = this.authenticationService.isTokenExpired();

      // For native platforms, be more lenient - auto-login if user exists and remember me was enabled
      if (this.isNative) {
        if (user && rememberMeEnabled && !tokenExpired) {
          // Token is valid, auto-login
          this.router.navigate([this.returnUrl]);
          return;
        } else if (rememberMeEnabled && tokenExpired) {
          // Token expired - try to auto re-login using saved credentials
          this.loading = true;
          const reLoginSuccess = await this.authenticationService.tryAutoReLogin();
          this.loading = false;
          if (reLoginSuccess) {
            this.router.navigate([this.returnUrl]);
            return;
          }
          // Re-login failed, show login form
        }
      } else {
        // Web platform - original behavior
        if (rememberMeEnabled && user && !tokenExpired) {
          this.router.navigate([this.returnUrl]);
          return;
        }
      }

      // No auto-login, clear session
      if (!rememberMeEnabled) {
        await this.authenticationService.logout();
      }
    } catch (e) {
      console.error('Error checking auto-login:', e);
      // Fall back to original behavior
      const rememberMeHistory = localStorage.getItem('remember');
      if (rememberMeHistory === 'true') {
        const user = this.authenticationService.currentUserValue;
        if (user) {
          this.router.navigate([this.returnUrl]);
          return;
        }
      }
      await this.authenticationService.logout();
    }
  }

  private initGoogleSignIn() {
    const clientId = environment.googleSignInClientId;
    if (!clientId || typeof google === 'undefined' || !google.accounts) {
      return;
    }
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response: any) => this.handleGoogleCredential(response),
    });
    this.googleInitialized = true;
  }

  private handleGoogleCredential(response: any) {
    if (!response || !response.credential) {
      return;
    }
    // GIS invokes this callback outside Angular's zone — run inside it so
    // navigation and view updates are picked up.
    this.ngZone.run(() => {
      this.loading = true;
      this.googleError = '';
      this.authenticationService.googleLogin(response.credential, this.f.rememberMe.value)
        .pipe(first())
        .subscribe(
          () => this.router.navigate([this.returnUrl]),
          error => {
            this.googleError = (error && error.detail) ? error.detail : 'Google sign-in failed. Please try again.';
            this.loading = false;
          });
    });
  }

  togglePasswordVisibility() {
    this.hide = !this.hide;
  }

  onGoogleSignIn() {
    if (!this.googleInitialized) {
      this.googleError = 'Google sign-in is unavailable right now. Please use your email and password.';
      return;
    }
    this.googleError = '';
    // Shows the Google account chooser / One Tap prompt.
    google.accounts.id.prompt();
  }

  // convenience getter for easy access to form fields
  get f() { return this.loginForm.controls; }

  get o() { return this.otpForm.controls; }

  /** Switch to phone sign-in. */
  usePhoneSignIn() {
    this.mode = 'phone';
    this.error = '';
    this.googleError = '';
    this.otpError = '';
    this.otpNotice = '';
    this.otpSubmitted = false;
    this.clearOtpBoxes();
    this.stopResendCountdown();
  }

  /** Back to email + password, discarding any half-finished code entry. */
  usePasswordSignIn() {
    this.mode = 'password';
    this.otpError = '';
    this.otpNotice = '';
    this.otpSubmitted = false;
    this.clearOtpBoxes();
    this.stopResendCountdown();
  }

  /**
   * Keep the number field numeric and in (617) 555-0100 shape as it is typed.
   *
   * Anything that is not a digit is dropped rather than rejected, so pasting
   * "+1 617-555-0100" or "617.555.0100" lands correctly instead of erroring.
   * A leading country code is only stripped once there are more than 10 digits,
   * so typing never makes a character the user just entered disappear.
   */
  onPhoneInput(event: any) {
    let digits = (event.target.value || '').replace(/\D/g, '');
    if (digits.length > 10 && digits.charAt(0) === '1') {
      digits = digits.substring(1);
    }
    digits = digits.substring(0, 10);

    let formatted = digits;
    if (digits.length > 6) {
      formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    } else if (digits.length > 3) {
      formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
    } else if (digits.length > 0) {
      formatted = `(${digits}`;
    }

    // Write back through the control so the view and the model agree.
    this.o.phoneNumber.setValue(formatted);
  }

  /** Sync visual cells from the real input value. Called on every keystroke. */
  onOtpRealInput() {
    // Strip non-digits and cap at 6.
    let raw = (this.o.otp.value || '').replace(/\D/g, '').substring(0, 6);
    this.o.otp.setValue(raw, { emitEvent: false });
    this.refreshOtpDigits(raw);
  }

  updateOtpCursor() {
    const val = (this.o.otp.value || '');
    this.otpCursor = Math.min(val.length, 5);
  }

  private refreshOtpDigits(val: string) {
    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = val.charAt(i) || '';
    }
    this.otpCursor = Math.min(val.length, 5);
  }

  /** Clear OTP. */
  private clearOtpBoxes() {
    this.o.otp.setValue('');
    this.refreshOtpDigits('');
  }

  /** Send (or resend) a passcode to the number entered. */
  requestOtp() {
    this.otpSubmitted = true;
    this.otpError = '';
    if (this.o.phoneNumber.errors) {
      return;
    }

    this.loading = true;
    this.authenticationService.requestLoginOtp(this.o.phoneNumber.value)
      .pipe(first())
      .subscribe(
        response => {
          this.loading = false;
          // The backend will not say whether the number is on an account, so
          // neither do we -- just move on to code entry.
          this.clearOtpBoxes();
          this.mode = 'otp';
          this.otpSubmitted = false;
          this.otpNotice = (response && response.detail)
            ? response.detail
            : 'If that number is on an account, a code is on its way.';
          this.startResendCountdown();
        },
        error => {
          this.loading = false;
          this.otpError = (error && error.detail)
            ? error.detail
            : 'Could not send a code. Please try again.';
        });
  }

  /** Exchange the entered code for a session. */
  verifyOtp() {
    this.otpSubmitted = true;
    this.otpError = '';
    if (this.otpForm.invalid) {
      return;
    }

    this.loading = true;
    this.authenticationService.verifyLoginOtp(
      this.o.phoneNumber.value, this.o.otp.value, this.f.rememberMe.value)
      .pipe(first())
      .subscribe(
        () => {
          this.stopResendCountdown();
          this.router.navigate([this.returnUrl]);
        },
        error => {
          this.loading = false;
          this.clearOtpBoxes();
          this.otpSubmitted = false;
          this.otpError = (error && error.detail)
            ? error.detail
            : 'That code is not valid. Please try again.';
        });
  }

  /** Disable the resend button for as long as the backend's cooldown lasts. */
  private startResendCountdown() {
    this.stopResendCountdown();
    this.resendSeconds = 30;
    this.resendTimer = setInterval(() => {
      this.resendSeconds -= 1;
      if (this.resendSeconds <= 0) {
        this.stopResendCountdown();
      }
    }, 1000);
  }

  private stopResendCountdown() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
    this.resendSeconds = 0;
  }

  ngOnDestroy() {
    this.stopResendCountdown();
  }

  /** One form, three modes -- send the submit to whichever step is showing. */
  onFormSubmit() {
    if (this.mode === 'phone') {
      this.requestOtp();
    } else if (this.mode === 'otp') {
      this.verifyOtp();
    } else {
      this.onSubmit();
    }
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.authenticationService.login(this.f.username.value, this.f.password.value, this.f.rememberMe.value)
      .pipe(first())
      .subscribe(
        data => {
          this.router.navigate([this.returnUrl]);
        },
        error => {
          this.error = error;
          this.loading = false;
        });
  }

}
